import React, { useState } from 'react';
import { createBusiness, updateBusiness } from './MiniERPPriceFlowAPI';

const CATEGORY_SUGGESTIONS = ['Hair', 'Facial', 'Spa', 'Cosmetics', 'Electrical', 'Hardware', 'Medicine', 'Textiles', 'Food', 'General'];

const inputCls =
  'w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-2 py-1 text-sm text-white placeholder-white/30 ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-500 transition';
const labelCls = 'flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-white';

const EMPTY = {
  businessName: '', slug: '', businessCategory: '', subCategories: '', address: '', city: '', state: '', country: '',
  phone: '', whatsapp: '', email: '', website: '', logo: '', about: '', workingHours: '', offerMessage: '',
};

// Full-page form — used both for first-time business creation and later edits.
// Pass `initial` (the business object) to edit; omit it to create a new one.
export default function MiniERPPriceFlowBusinessProfile({ initial, onSaved, onBack }) {
  const [form, setForm] = useState(() =>
    initial
      ? { ...EMPTY, ...initial, subCategories: (initial.subCategories || []).join(', ') }
      : EMPTY
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.businessName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, subCategories: form.subCategories.split(',').map((s) => s.trim()).filter(Boolean) };
      const saved = initial ? await updateBusiness(initial._id, payload) : await createBusiness(payload);
      onSaved && onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B12] text-white px-4 py-6 sm:px-8 sm:py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="w-fit px-2 py-1 text-sm text-white/50 hover:text-white transition">← Back</button>
          <h2 className="text-xl font-bold tracking-tight">{initial ? 'Edit Business' : 'Create Your Live Price List'}</h2>
          <span className="w-12" />
        </div>

        {!initial && (
          <p className="text-sm text-cyan-200 mb-6 text-center">
            Two minutes, one link, no more forwarding PDFs. 🚀
          </p>
        )}

        {error && <p className="text-rose-400 text-sm mb-4 text-center">{error}</p>}

        <form onSubmit={submit} className="rounded-3xl bg-[#14141C] ring-1 ring-white/10 p-6 space-y-4">
          <label className={labelCls}>
            Business Name *
            <input className={inputCls} value={form.businessName} onChange={set('businessName')} required placeholder="e.g. ABC Beauty Studio" />
          </label>

          <label className={labelCls}>
            Custom Link (optional)
            <div className="flex items-center gap-1">
              <span className="text-gray-100 text-sm whitespace-nowrap">think5whys.in/p/</span>
              <input className={inputCls} value={form.slug} onChange={set('slug')} placeholder="auto-generated if left blank" />
            </div>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelCls}>
              Category
              <select className={inputCls} value={form.businessCategory} onChange={set('businessCategory')}>
                <option value="" className="bg-[#14141C]">Select category</option>
                {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} className="bg-[#14141C]">{c}</option>)}
              </select>
            </label>
            <label className={labelCls}>
              Sub Categories <input className={inputCls} value={form.subCategories} onChange={set('subCategories')} placeholder="comma separated" />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className={labelCls}>City <input className={inputCls} value={form.city} onChange={set('city')} /></label>
            <label className={labelCls}>State <input className={inputCls} value={form.state} onChange={set('state')} /></label>
            <label className={labelCls}>Country <input className={inputCls} value={form.country} onChange={set('country')} /></label>
          </div>

          <label className={labelCls}>Address <input className={inputCls} value={form.address} onChange={set('address')} /></label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelCls}>Phone <input className={inputCls} value={form.phone} onChange={set('phone')} /></label>
            <label className={labelCls}>WhatsApp <input className={inputCls} value={form.whatsapp} onChange={set('whatsapp')} placeholder="for the WhatsApp button" /></label>
            <label className={labelCls}>Email <input type="email" className={inputCls} value={form.email} onChange={set('email')} /></label>
            <label className={labelCls}>Website <input className={inputCls} value={form.website} onChange={set('website')} /></label>
          </div>

          <label className={labelCls}>Logo URL <input className={inputCls} value={form.logo} onChange={set('logo')} placeholder="https://..." /></label>
          <label className={labelCls}>Working Hours <input className={inputCls} value={form.workingHours} onChange={set('workingHours')} placeholder="e.g. Mon–Sat, 10am–8pm" /></label>
          <label className={labelCls}>About <textarea style={{height: "100px"}} className={inputCls} value={form.about} onChange={set('about')} /></label>
          <label className={labelCls}>
            Offer Message <input className={inputCls} value={form.offerMessage} onChange={set('offerMessage')} placeholder="e.g. Flat 20% off this week!" />
          </label>

          <button
            type="submit"
            disabled={saving}
            className="w-fit px-2 py-1 mt-2 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-emerald-500
                       hover:from-cyan-400 hover:to-emerald-400 shadow-lg shadow-emerald-900/30 disabled:opacity-50 transition"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Create Business Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}
